import React, { useState } from "react";

import { StatCard } from "./stat-card";
import { IMemorizationStats } from "./models/dtos";

const TOTAL_QURAN_AYAT = 6236;
const TOTAL_MADANI_PAGES = 604;

export type MemorizationProgressStatProps = {
    memorizationStats: IMemorizationStats | null;
}

export function MemorizationProgressStat( props: MemorizationProgressStatProps) {

  const {memorizationStats} = props;
  const [showMadaniInfo, setShowMadaniInfo] = useState(false);
  
  if (!memorizationStats) {
    return <div>Ingen færdige memoreringer fundet.</div>;
  }

  const isBehind = memorizationStats.behindPct > 0;

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "grid",
          gap: 12,
          marginBottom: 20,
          maxHeight: "280px",
          overflowY: "auto",
          paddingRight: "4px",
        }}
      >
        <div
          style={{
            marginBottom: "2px",
            fontSize: "13px",
            lineHeight: 1.45,
            color: "#374151",
            backgroundColor: "#f0f9f7",
            border: "1px solid #ccece7",
            borderRadius: "8px",
            padding: "8px 10px",
            paddingRight: "36px",
            position: "relative",
          }}
        >
          <strong>MADANI Mushaf:</strong> som er på 604 sider er brugt til at beregne fremdrift forneden.
          <button
            type="button"
            onClick={() => setShowMadaniInfo((prev) => !prev)}
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              width: "18px",
              height: "18px",
              borderRadius: "50%",
              border: "1px solid #197770",
              background: "#fff",
              color: "#197770",
              fontSize: "12px",
              fontWeight: 700,
              lineHeight: "16px",
              textAlign: "center",
              cursor: "pointer",
            }}
            aria-label="Vis arabisk beskrivelse"
            title="Vis arabisk beskrivelse"
          >
            i
          </button>

          {showMadaniInfo && (
            <div
              style={{
                position: "absolute",
                top: "36px",
                right: "10px",
                maxWidth: "280px",
                background: "#f8fafc",
                border: "1px solid #dbe4ee",
                borderRadius: "8px",
                padding: "8px 10px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                fontSize: "13px",
                lineHeight: 1.5,
                zIndex: 6,
              }}
            >
              <div style={{ color: "#334155", marginBottom: "8px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#0f766e", marginBottom: "2px" }}>
                  Dansk
                </div>
                <div>
                  MADANI Mushaf er pa 604 sider, og dette tal bruges til at beregne fremdrift i sider.
                </div>
              </div>
              <div style={{ color: "#334155", marginBottom: "8px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#0f766e", marginBottom: "2px" }}>
                  English
                </div>
                <div>
                  Madani Mushaf contains 604 pages, and this total is used to calculate page-based memorization progress.
                </div>
              </div>
              <div style={{ color: "#334155", direction: "rtl", textAlign: "right" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#0f766e", marginBottom: "2px" }}>
                  العربية
                </div>
                <div>
                  مصحف المدينة يحتوي على ٦٠٤ صفحات، ويُستخدم هذا العدد لحساب نسبة التقدم في الحفظ بالصفحات.
                </div>
              </div>
            </div>
          )}
        </div>
        
       { <StatCard
          title="Planlagt færdigdato"
          value={memorizationStats.plannedFinishDate}
         danishDescription="Planlagt færdigdato: Datoen hvor eleven forventes at være færdig med memoreringen ifølge den valgte plan."
         englishDescription="Planned finish date: The date the student is expected to complete memorization according to the approved study plan."
          arabicDescription="تاريخ الإنهاء المخطط: هذا هو التاريخ الذي يُفترض أن يُتم فيه الطالب الحفظ حسب الخطة الزمنية المعتمدة."
        /> }

        {<StatCard
          title="Forventet færdigdato"
          value={memorizationStats.forecastFinishDate}
          danishDescription="Forventet færdigdato: Estimeret slutdato baseret på elevens nuværende memoreringshastighed."
          englishDescription="Forecast finish date: An estimated completion date based on the student's current memorization pace."
          arabicDescription="تاريخ الإنهاء المتوقع: هذا التاريخ تقديري بناءً على سرعة تقدم الطالب الحالية في الحفظ."
        />}

        {<StatCard
          title="Status i dag"
          value={
            isBehind
              ? `${Math.abs(memorizationStats.behindPct)}% bag planen`
              : `${Math.abs(memorizationStats.behindPct)}% foran planen`
          }
          subtitle={`Faktisk: ${memorizationStats.latestMemorizationPoint.ayatProgressPct}% · Plan i dag: ${memorizationStats.plannedPctToday}%`}
          valueColor={isBehind ? "#dc2626" : "#16a34a"}
          danishDescription="Status i dag: Viser om eleven lige nu er bagud eller foran i forhold til den planlagte fremdrift."
          englishDescription="Status today: Shows whether the student is currently behind or ahead compared to the planned progress."
          arabicDescription="الحالة اليوم: توضح إن كان الطالب متأخراً أو متقدماً مقارنة بالخطة في الوقت الحالي."
        />}

        {<StatCard
          title="Nuværende memorerings tempo pr. dag"
          value={`${memorizationStats.ayatPerDay} ayat · ${memorizationStats.pagesPerDay} sider`}
          subtitle={`Beregnet ud fra de seneste data over ${memorizationStats.measuredDays} dage`}
          valueColor="#0f766e"
          danishDescription="Nuværende tempo pr. dag: Gennemsnit af hvor meget eleven memorerer dagligt i ayat og sider baseret på de nyeste data."
          englishDescription="Current daily memorization pace: The student's average daily memorization in ayat and pages based on the latest available data."
          arabicDescription="الوتيرة الحالية يومياً: متوسط ما يحفظه الطالب كل يوم من الآيات والصفحات بناءً على أحدث البيانات."
        />}

        {isBehind && <StatCard
          title="Nødvendigt memorerings tempo pr. dag hvis elev skal nå planlagt færdigdato"
          value={
            memorizationStats.requiredAyatPerDayToFinishOnPlan !== null &&
            memorizationStats.requiredPagesPerDayToFinishOnPlan !== null
              ? `${memorizationStats.requiredAyatPerDayToFinishOnPlan} ayat · ${memorizationStats.requiredPagesPerDayToFinishOnPlan} sider`
              : "Planlagt faerdigdato er passeret"
          }
          subtitle={
            memorizationStats.remainingDaysToPlannedFinish > 0
              ? `Du har ${memorizationStats.remainingDaysToPlannedFinish} dage tilbage til planlagt færdigdato`
              : "Juster startdato eller plan for at få et realistisk tempo"
          }
          valueColor="#dc2626"
          danishDescription="Nødvendigt tempo pr. dag: Antal ayat og sider eleven skal memorere dagligt for stadig at nå planlagt færdigdato."
          englishDescription="Required daily pace: The number of ayat and pages that must be memorized each day to still reach the planned finish date."
          arabicDescription="الوتيرة المطلوبة يومياً: عدد الآيات والصفحات التي يجب حفظها يومياً للوصول إلى تاريخ الإنهاء المخطط."
        />}

        {<StatCard
          title="Færdig i forhold til ayat"
          value={`${memorizationStats.latestMemorizationPoint.ayatProgressPct}%`}
          subtitle={`${memorizationStats.latestMemorizationPoint.memorizedAyat} af ${TOTAL_QURAN_AYAT} ayat · ${memorizationStats.latestMemorizationPoint.displayText}`}
          valueColor="#2563eb"
          danishDescription="Færdig i forhold til ayat: Viser hvor stor en procentdel af alle Quran-ayat der er memoreret indtil nu."
          englishDescription="Progress by ayat: The percentage of total Quran ayat that the student has memorized so far."
          arabicDescription="نسبة الإنجاز بالآيات: نسبة ما تم حفظه من مجموع آيات القرآن حتى الآن."
        />}

        {<StatCard
          title="Færdig i forhold til sider"
          value={`${memorizationStats.latestMemorizationPoint.pageProgressPct}%`}
          subtitle={`${memorizationStats.latestMemorizationPoint.memorizedPages} af ${TOTAL_MADANI_PAGES} sider`}
          valueColor="#7c3aed"
          danishDescription="Færdig i forhold til sider: Viser hvor stor en andel af siderne i Madani Mushaf (604 sider) der er gennemført."
          englishDescription="Progress by pages: The percentage of completed pages based on the Madani Mushaf (604 pages)."
          arabicDescription="نسبة الإنجاز بالصفحات: نسبة ما تم حفظه من صفحات مصحف المدينة (604 صفحة)."
        />}

        {<StatCard
          title="Hvor meget mangler?"
          value={`${memorizationStats.latestMemorizationPoint.ayatRemainingPct}% ayat · ${memorizationStats.latestMemorizationPoint.pageRemainingPct}% sider`}
          subtitle={`${memorizationStats.latestMemorizationPoint.remainingAyat} ayat mangler · ${memorizationStats.latestMemorizationPoint.remainingPages} sider mangler`}
          valueColor="#f59e0b"
          danishDescription="Hvor meget mangler: Viser både procent og antal ayat/sider, der stadig mangler for at blive færdig."
          englishDescription="Remaining work: Shows both the percentage and count of ayat and pages left to complete memorization."
          arabicDescription="المتبقي: يوضح نسبة وعدد الآيات والصفحات المتبقية لإكمال الحفظ."
        />}
      </div>
    </div>
  );
}